const decodeRepeatedly = (value, maxPasses = 3) => {
  let current = String(value || '');
  for (let i = 0; i < maxPasses; i += 1) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
    } catch (_) {
      break;
    }
  }
  return current;
};

export const normalizeStorageKey = (input) => {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  let candidate = trimmed;
  try {
    const parsed = new URL(trimmed);
    candidate = parsed.pathname || trimmed;
  } catch (_) {
    // Not a full URL; keep the original
  }

  candidate = decodeRepeatedly(candidate);

  if (candidate.startsWith('/uploads/')) {
    candidate = candidate.slice('/uploads/'.length);
  }

  const envIndex = candidate.indexOf('/env=prod/');
  if (envIndex !== -1) {
    candidate = candidate.slice(envIndex + 1);
  }

  const sessionsIndex = candidate.indexOf('/sessions/');
  if (!candidate.startsWith('env=prod/') && sessionsIndex !== -1) {
    candidate = candidate.slice(sessionsIndex + 1);
  }

  if (candidate.startsWith('/')) {
    candidate = candidate.slice(1);
  }

  return candidate || null;
};

/**
 * Roots the storage providers actually generate keys under.
 * @see backend/src/services/storage/S3StorageProvider.js generateKey
 */
const STORAGE_ROOTS = ['sessions/', 'env=prod/', 'users/'];

/**
 * Does this look like a key issued by the storage layer, as opposed to any old
 * path that happens to be a URL?
 */
export const looksLikeStorageKey = (key) => (
  typeof key === 'string' && STORAGE_ROOTS.some((root) => key.startsWith(root))
);

/**
 * Derive a storage key from a URL, or null when the URL does not belong to the
 * storage layer at all.
 *
 * normalizeStorageKey answers "what would this path be as a key", which for a
 * bundled asset like /static/media/x.mp3 confidently returns
 * "static/media/x.mp3". Callers then test that against session-scoped prefixes
 * and reject it — so a perfectly valid non-storage URL gets treated as another
 * session's file and silently dropped. Returning null here keeps the
 * "unscoped content is allowed" branch reachable, which is what the callers
 * already intend.
 */
export const storageKeyFromUrl = (url) => {
  const key = normalizeStorageKey(url);
  return looksLikeStorageKey(key) ? key : null;
};
